/**
 * Phase 2.3: Gesture Conversion Utilities
 * Convert 2D touch gestures to 3D camera navigation
 * 
 * Core workflow:
 * 1. swipeToWorldDirection() - Convert 2D pan delta to 3D direction using camera rotation
 * 2. findCamerasInDirection() - Find cameras within a cone in that direction
 * 3. scoreCandidates() - Rank candidates by spatial proximity (60%) + continuity (40%)
 */

import { Vector3, Quaternion } from './vectorMath'
import { Camera } from '../types/colmap'
import { PointCloudMappings } from './pointCloudMappings'
import {
  normalize,
  dot,
  distance,
  rotateVectorByQuaternion,
} from './vectorMath'

/**
 * FUNCTION 1: Convert 2D swipe to 3D world direction
 * 
 * Maps screen-space pan (swipeX, swipeY in pixels) to 3D world direction
 * using the camera's rotation (COLMAP quaternion format).
 * 
 * Algorithm:
 * 1. Normalize 2D swipe vector
 * 2. Convert to 3D camera-space direction (x=right, y=down, z=forward)
 * 3. Rotate by camera's inverse rotation to get world-space direction
 * 4. Return normalized world direction
 * 
 * @param swipeX - Horizontal pan delta (pixels, positive=right)
 * @param swipeY - Vertical pan delta (pixels, positive=down)
 * @param cameraRotation - Camera rotation quaternion [w, x, y, z]
 * @returns Normalized 3D direction vector in world space
 */
export function swipeToWorldDirection(
  swipeX: number,
  swipeY: number,
  cameraRotation: Quaternion
): Vector3 {
  // Map 2D screen coordinates to 3D camera-space direction
  // Camera space: x=right, y=down, z=forward (looking toward +z)
  // Note: negating y because screen y increases downward, but we want z-axis rotation
  const cameraSpaceDirection: Vector3 = [
    swipeX,
    -swipeY, // Invert: screen down = camera up (rotation around x-axis)
    0, // Pan doesn't move forward/backward
  ]

  // Normalize to unit vector for consistent cone angle
  const normalizedDirection = normalize(cameraSpaceDirection)

  // Rotate camera-space direction by camera's inverse rotation to get world-space direction
  // The camera rotation tells us how the camera is oriented in world space
  // To convert camera-space direction to world-space, we apply the camera rotation
  const worldDirection = rotateVectorByQuaternion(normalizedDirection, cameraRotation)

  return normalize(worldDirection)
}

/**
 * FUNCTION 2: Find cameras in a cone in the given direction
 * 
 * Searches for cameras within a cone from the current camera position,
 * pointing in the specified direction.
 * 
 * Algorithm:
 * 1. Filter cameras by max distance (spatial constraint)
 * 2. For each camera, calculate vector from current to candidate
 * 3. Compute angle between direction vector and candidate vector
 * 4. Keep cameras where angle < cone angle (using dot product)
 * 5. Return all candidates
 * 
 * Performance: O(n) where n = number of cameras
 * 
 * @param cameraPosition - Current camera position [x, y, z] in world space
 * @param direction - Direction vector (should be normalized)
 * @param cameras - All available cameras
 * @param coneAngleDegrees - Half-angle of cone (default 45°)
 * @param maxDistance - Maximum distance to search (default 2.0m)
 * @returns Cameras within the cone
 */
export function findCamerasInDirection(
  cameraPosition: Vector3,
  direction: Vector3,
  cameras: Camera[],
  coneAngleDegrees: number = 45,
  maxDistance: number = 2.0
): Camera[] {
  const coneCosThreshold = Math.cos((coneAngleDegrees * Math.PI) / 180) // cos(angle)

  const candidates: Camera[] = []
  const debugCandidates: Array<{id: number; dist: number; angle: number; dot: number}> = []

  for (const camera of cameras) {
    // Skip if candidate is the same as current (or very close)
    if (
      Math.abs(camera.position[0] - cameraPosition[0]) < 0.01 &&
      Math.abs(camera.position[1] - cameraPosition[1]) < 0.01 &&
      Math.abs(camera.position[2] - cameraPosition[2]) < 0.01
    ) {
      continue
    }

    // Vector from current to candidate
    const toCandidate: Vector3 = [
      camera.position[0] - cameraPosition[0],
      camera.position[1] - cameraPosition[1],
      camera.position[2] - cameraPosition[2],
    ]

    // Distance check (first, for performance)
    const dist = distance(cameraPosition, camera.position)

    if (dist > maxDistance) {
      continue // Too far, skip
    }

    // Normalize candidate vector
    const normalizedToCandidate = normalize(toCandidate)

    // Dot product of direction and toCandidate
    // If dot > cos(angle), candidate is within cone
    const dotProduct = dot(direction, normalizedToCandidate)
    const angleRad = Math.acos(Math.max(-1, Math.min(1, dotProduct))) // Clamp to avoid NaN
    const angleDeg = (angleRad * 180) / Math.PI

    // DEBUG: Track all candidates for logging
    debugCandidates.push({ id: camera.id, dist, angle: angleDeg, dot: dotProduct })

    if (dotProduct >= coneCosThreshold) {
      candidates.push(camera)
    }
  }

  // Silently skip detailed logging in production
  return candidates
}

/**
 * FUNCTION 3: Score and rank candidate cameras
 * 
 * Ranks cameras by two criteria:
 * - Spatial proximity: 60% weight (closer is better)
 * - Continuity: 40% weight (more shared points is better)
 * 
 * Algorithm:
 * 1. Calculate distance score: 1 - (distance / maxDistance)
 *    - Clamp to [0,1] where 1=closest, 0=farthest
 * 2. Calculate continuity score: sharedPoints / totalPoints
 *    - Count shared points between current and candidate
 *    - Normalize by max shared points in the set
 * 3. Combine: score = 0.6 * spatial + 0.4 * continuity
 * 4. Sort by score (descending)
 * 
 * Performance: O(n + p) where n=candidates, p=point lookups
 * 
 * @param currentCameraId - Current camera ID
 * @param candidates - Candidate cameras to score
 * @param pointCloudMappings - Camera-point relationships (cameraToPoints map)
 * @param weights - Score weights (optional, default 0.6 spatial, 0.4 continuity)
 * @returns Scored cameras sorted by score (highest first)
 */
export function scoreCandidates(
  currentCameraId: number,
  candidates: Camera[],
  pointCloudMappings: PointCloudMappings,
  weights?: { spatial: number; continuity: number }
): Array<{ camera: Camera; score: number }> {
  const spatialWeight = weights?.spatial ?? 0.6
  const continuityWeight = weights?.continuity ?? 0.4

  // Maximum distance for spatial scoring (normalize factor)
  const MAX_DISTANCE = 2.0

  // Get current camera's points for continuity scoring
  const currentCameraPoints = pointCloudMappings.cameraToPoints.get(currentCameraId) || new Set()

  // Calculate max possible shared points (for normalization)
  let maxSharedPoints = 0
  for (const candidate of candidates) {
    const candidatePoints = pointCloudMappings.cameraToPoints.get(candidate.id) || new Set()

    // Count intersection (shared points)
    let sharedCount = 0
    for (const pointId of currentCameraPoints) {
      if (candidatePoints.has(pointId)) {
        sharedCount++
      }
    }

    if (sharedCount > maxSharedPoints) {
      maxSharedPoints = sharedCount
    }
  }

  // Prevent division by zero
  if (maxSharedPoints === 0) {
    maxSharedPoints = 1
  }

  const scored = candidates.map((camera) => {
    // SPATIAL SCORE: proximity (60% weight)
    // Distance from first candidate (arbitrary reference point)
    const dist = distance(camera.position, candidates[0].position)

    // Normalize distance: 0 at max_distance, 1 at distance 0
    const spatialScore = Math.max(0, 1 - dist / MAX_DISTANCE)

    // CONTINUITY SCORE: shared points (40% weight)
    const candidatePoints = pointCloudMappings.cameraToPoints.get(camera.id) || new Set()

    // Count intersection (shared points)
    let sharedCount = 0
    for (const pointId of currentCameraPoints) {
      if (candidatePoints.has(pointId)) {
        sharedCount++
      }
    }

    const continuityScore = maxSharedPoints > 0 ? sharedCount / maxSharedPoints : 0

    // COMBINED SCORE: weighted average
    const score = spatialWeight * spatialScore + continuityWeight * continuityScore

    return { camera, score }
  })

  // Sort by score (highest first)
  scored.sort((a, b) => b.score - a.score)

  return scored
}

/**
 * HELPER: Get direction name for logging
 * Useful for debug output to understand pan direction
 */
export function getDirectionName(swipeX: number, swipeY: number): string {
  const isHorizontal = Math.abs(swipeX) > Math.abs(swipeY)

  if (isHorizontal) {
    return swipeX > 0 ? 'RIGHT' : 'LEFT'
  } else {
    return swipeY > 0 ? 'DOWN' : 'UP'
  }
}
